import { inScopeIntegrations, parseTechStackSystems } from '../system-landscape/tech-stack.js';
import { countsTowardEstimate } from '../types/scope.js';
/**
 * "No scope captured" generation gate (COS-100, docs/plans/72).
 *
 * On an AI-mode deal category the estimate is built ONLY from the deal's ad-hoc scope items
 * (the "Capture questions" step) plus in-scope Current Technology Stack integrations - the
 * questionnaire rules do not run. With neither, every generation is a silent 0 h estimate and
 * a 0 h SOW (915 Advisory, "Zanderman", 2026-10-06). The API refuses those runs and the deal
 * page disables Generate, both from this one predicate so they cannot disagree.
 */
export const NO_SCOPE_CAPTURED_CODE = 'NO_SCOPE_CAPTURED';
/** The one plain sentence the API, the deal page, and the MCP tools all show. */
export const NO_SCOPE_CAPTURED_MESSAGE = 'No priced scope items yet. Add scope to this deal first, then generate.';
/**
 * AI-mode categories whose generation is NOT blocked even with no captured scope: managed
 * services builds its SOW from the contract terms and refills contract fields from the
 * estimate, so a no-scope estimate there may be intentional. Unblocked until confirmed otherwise.
 */
export const NO_SCOPE_GATE_EXEMPT_CATEGORY_KEYS = new Set([
    'managed_services',
]);
/** True when generation must be refused because nothing on the deal can produce hours. */
export function isGenerationBlockedForNoScope(input) {
    if (!input.aiMode)
        return false;
    if (input.categoryKey && NO_SCOPE_GATE_EXEMPT_CATEGORY_KEYS.has(input.categoryKey))
        return false;
    if (input.primaryScopeItemCount > 0)
        return false;
    return inScopeIntegrations(parseTechStackSystems(input.techStackSystemsAnswer)).length === 0;
}
/**
 * Field keys of the ad-hoc scope items that carry an hours override in a saved estimate's
 * `overrides` map. Ad-hoc lines are keyed `subsection:ad_hoc_scope:<fieldKey>` or, once split by
 * functional area, `subsection:ad_hoc_scope__group__<slug>:<fieldKey>` - the same match the
 * generation run uses to carry overrides forward.
 */
export function adhocOverrideFieldKeys(overrides) {
    const keys = new Set();
    if (!overrides || typeof overrides !== 'object')
        return keys;
    for (const [k, v] of Object.entries(overrides)) {
        if (v == null)
            continue;
        const m = k.match(/^subsection:ad_hoc_scope(?:__group__[a-z0-9_]+)?:(.+)$/);
        if (m)
            keys.add(m[1]);
    }
    return keys;
}
/**
 * How many PRIMARY scope items count toward the estimate: costed, or overridden by a user
 * (`countsTowardEstimate` - the one shared "line counts" rule, so the gate, the estimate total and
 * the SOW line items agree). A deal whose only items are 'mentioned' / 'ongoing' counts 0.
 */
export function countScopeItemsThatCount(items, overriddenFieldKeys = new Set()) {
    return items.filter((i) => countsTowardEstimate(i.scopeStatus, overriddenFieldKeys.has(i.fieldKey))).length;
}
//# sourceMappingURL=scope-capture-gate.js.map